import { ProductReviewStatus, ProductStatus } from '@shopify-multiseller/graphql/partner/types';
import { Badge, type BadgeProps } from '../primitives';

export interface ProductState {
  label: string;
  variant: NonNullable<BadgeProps['variant']>;
}

/**
 * A product has two statuses — where it is in the shop's review and whether it
 * is live — and a seller has one question: can people buy this, and if not,
 * whose move is it? Two badge columns made them answer that by cross-referencing.
 * This resolves both into the one state that answers it.
 */
export const productState = (product: {
  status?: ProductStatus | null;
  reviewStatus?: ProductReviewStatus | null;
}): ProductState => {
  if (product.status === ProductStatus.Archived) {
    return { label: 'Archived', variant: 'neutral' };
  }
  if (
    product.reviewStatus === ProductReviewStatus.Rejected ||
    product.reviewStatus === ProductReviewStatus.ChangesRejected
  ) {
    return { label: 'Changes requested', variant: 'destructive' };
  }
  // Two different situations, and the design names them apart. A product
  // waiting on its first review is not live at all; a live product with edits
  // waiting is still selling, and telling its seller the *product* is awaiting
  // approval reads as though it had been taken down. What is pending is the
  // changes.
  if (product.reviewStatus === ProductReviewStatus.ChangesPending) {
    return { label: 'Changes pending review', variant: 'warning' };
  }
  if (product.reviewStatus === ProductReviewStatus.Pending) {
    return { label: 'Awaiting approval', variant: 'warning' };
  }
  if (product.status === ProductStatus.Active) {
    return { label: 'Active', variant: 'success' };
  }
  return { label: 'Offline', variant: 'neutral' };
};

export const ProductStateBadge: React.FC<{
  status?: ProductStatus | null;
  reviewStatus?: ProductReviewStatus | null;
}> = ({ status, reviewStatus }) => {
  const state = productState({ status, reviewStatus });
  return (
    <Badge variant={state.variant} dot data-testid="product-state">
      {state.label}
    </Badge>
  );
};
